<template>
  <div class="modal fade show d-block" tabindex="-1" style="">
    <div class="modal-dialog modal-xl">
      <div class="modal-content">
        <div class="modal-header bg-white text-dark">
          <h5 class="modal-title">
            <AppIcon name="bi-folder" class="bi bi-folder me-2" />
            {{ action === 'create' ? $t('create_project') : $t('edit_project') }}
          </h5>
          <button type="button" class="btn-close btn-close-white" @click="close"></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="submit">
            <div class="row">
              <!-- Left Column: Basic Information -->
              <div class="col-md-7">
                <div class="card mb-3 border-0 shadow-sm h-100">
                  <div class="card-header bg-light">
                    <h6 class="mb-0"><AppIcon name="bi-info-circle" class="bi bi-info-circle me-2" />{{$t('basic_information')}}</h6>
                  </div>
                  <div class="card-body">
                    <div class="mb-3">
                      <label class="form-label fw-bold">{{$t('name')}} *</label>
                      <input type="text" class="form-control" v-model="form.name" required placeholder="Enter project name">
                    </div>

                    <div class="mb-3">
                      <label class="form-label fw-bold">{{$t('client')}}</label>
                      <auto-complete
                        entity="clients"
                        emitEvent="emitAutoCompleteClient"
                        :defaultValue="clientName"
                        @emitAutoCompleteClient="emitAutoCompleteClient"
                      ></auto-complete>
                    </div>

                    <div class="row">
                      <div class="col-md-4 mb-3">
                        <label class="form-label fw-bold">{{$t('status')}}</label>
                        <select class="form-select" v-model="form.status">
                          <option value="draft">{{$t('draft')}}</option>
                          <option value="active">{{$t('active')}}</option>
                          <option value="on_hold">{{$t('on_hold')}}</option>
                          <option value="completed">{{$t('completed')}}</option>
                          <option value="cancelled">{{$t('cancelled')}}</option>
                        </select>
                      </div>
                      <div class="col-md-4 mb-3">
                        <label class="form-label fw-bold">{{$t('priority')}}</label>
                        <select class="form-select" v-model="form.priority">
                          <option value="low">{{$t('low')}}</option>
                          <option value="medium">{{$t('medium')}}</option>
                          <option value="high">{{$t('high')}}</option>
                          <option value="urgent">{{$t('urgent')}}</option>
                        </select>
                      </div>
                      <div class="col-md-4 mb-3">
                        <label class="form-label fw-bold">{{$t('budget')}}</label>
                        <input type="number" class="form-control" v-model="form.budget" step="0.01" placeholder="0.00">
                      </div>
                    </div>

                    <div class="mb-3">
                      <label class="form-label fw-bold">{{$t('description')}}</label>
                      <textarea class="form-control" v-model="form.description" rows="2" placeholder="Describe the project..."></textarea>
                    </div>

                    <div class="row">
                      <div class="col-md-6 mb-3">
                        <label class="form-label fw-bold">{{$t('start_date')}}</label>
                        <input type="date" class="form-control" v-model="form.start_date">
                      </div>
                      <div class="col-md-6 mb-3">
                        <label class="form-label fw-bold">{{$t('end_date')}}</label>
                        <input type="date" class="form-control" v-model="form.end_date">
                      </div>
                    </div>

                    <div class="row" v-if="action === 'update'">
                      <div class="col-md-6 mb-3">
                        <label class="form-label fw-bold">{{$t('actual_start_date')}}</label>
                        <input type="date" class="form-control" v-model="form.actual_start_date">
                      </div>
                      <div class="col-md-6 mb-3">
                        <label class="form-label fw-bold">{{$t('actual_end_date')}}</label>
                        <input type="date" class="form-control" v-model="form.actual_end_date">
                      </div>
                    </div>

                    <div class="mb-0">
                      <collapsible-notes-field v-model="form.notes" id="project-notes" rows="2" />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Right Column: Team Members -->
              <div class="col-md-5">
                <div class="card mb-3 border-0 shadow-sm">
                  <div class="card-header bg-light">
                    <h6 class="mb-0"><AppIcon name="bi-people" class="bi bi-people me-2" />{{$t('team_members')}}</h6>
                  </div>
                  <div class="card-body">
                    <div v-if="form.team_members.length === 0" class="text-muted small text-center py-2 mb-3">
                      <AppIcon name="bi-person-plus" class="bi bi-person-plus" /> {{$t('no_members_assigned')}}
                    </div>
                    <div v-for="(member, index) in form.team_members" :key="index" class="mb-2">
                      <div class="row g-2 align-items-center">
                        <div class="col-7">
                          <select class="form-select form-select-sm" v-model="member.user_id" required>
                            <option value="">{{$t('select_user')}}</option>
                            <option v-for="user in getAvailableUsers(index)" :key="user.id" :value="user.id">
                              {{ user.name }}
                            </option>
                          </select>
                        </div>
                        <div class="col-4">
                          <select class="form-select form-select-sm" v-model="member.role">
                            <option value="viewer">{{$t('viewer')}}</option>
                            <option value="contributor">{{$t('contributor')}}</option>
                            <option value="manager">{{$t('manager')}}</option>
                            <option value="owner">{{$t('owner')}}</option>
                          </select>
                        </div>
                        <div class="col-1">
                          <button type="button" class="btn btn-sm btn-primary" @click="removeMember(index)">
                            <AppIcon name="bi-x" class="bi bi-x" />
                          </button>
                        </div>
                      </div>
                    </div>
                    <button type="button" class="btn btn-outline-primary btn-sm w-100" @click="addMember">
                      <AppIcon name="bi-plus-circle" class="bi bi-plus-circle me-2" />{{$t('add_member')}}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="close">{{$t('cancel')}}</button>
          <button type="button" class="btn btn-success" @click="submit" :disabled="loading">
            <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
            {{ action === 'create' ? $t('create') : $t('save') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import helper from '../../helper'
import AutoComplete from '../autocomplete/AutoComplete'

export default {
  name: 'AddEditProjectModal',
  components: {
    AutoComplete
  },
  props: {
    isModalOpen: Boolean,
    data: {
      type: Object,
      default: () => ({})
    },
    action: {
      type: String,
      default: 'create'
    }
  },
  data() {
    return {
      form: {
        name: '',
        description: '',
        status: 'draft',
        priority: 'medium',
        budget: 0,
        start_date: '',
        end_date: '',
        actual_start_date: '',
        actual_end_date: '',
        progress: 0,
        notes: '',
        client_id: '',
        team_members: []
      },
      users: [],
      clientName: '',
      loading: false
    }
  },
  mounted() {
    this.loadUsers()
  },
  watch: {
    data: {
      handler(newData) {
        if (this.action === 'update' && newData && newData.id) {
          // Build team_members from assignments upfront so Vue 2 tracks it reactively
          const teamMembers = (newData.assignments && newData.assignments.length)
            ? newData.assignments.map(a => ({ user_id: a.user_id, role: a.role }))
            : []

          this.form = {
            name: newData.name || '',
            description: newData.description || '',
            status: newData.status || 'draft',
            priority: newData.priority || 'medium',
            budget: newData.budget || 0,
            start_date: this.formatDateForInput(newData.start_date),
            end_date: this.formatDateForInput(newData.end_date),
            actual_start_date: this.formatDateForInput(newData.actual_start_date),
            actual_end_date: this.formatDateForInput(newData.actual_end_date),
            progress: newData.progress || 0,
            notes: newData.notes || '',
            client_id: newData.client_id || '',
            team_members: teamMembers
          }

          // Set client name if client exists
          if (newData.client) {
            this.clientName = newData.client.name
          }
        }
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    async loadUsers() {
      try {
        const response = await helper.customGet(`${process.env.VUE_APP_API}/project-management/users`)
        this.users = response.data || []
      } catch (error) {
        alert('Error loading users:', error)
      }
    },
    emitAutoCompleteClient(data) {
      this.form.client_id = data.result.id
      this.clientName = data.result.name
    },
    addMember() {
      this.form.team_members.push({
        user_id: '',
        role: 'contributor'
      })
    },
    removeMember(index) {
      this.form.team_members.splice(index, 1)
    },
    async submit() {
      const requiredPermission = this.action === 'create' ? 'create_projects' : 'edit_projects'
      if (helper.checkAccess(requiredPermission) <= 0) return
      this.loading = true
      try {
        // Sanitize date fields - convert empty/invalid values to null
        const payload = { ...this.form }
        const dateFields = ['start_date', 'end_date', 'actual_start_date', 'actual_end_date']
        dateFields.forEach(field => {
          const val = payload[field]
          if (!val || val === '' || val === 'Invalid date' || isNaN(new Date(val).getTime())) {
            payload[field] = null
          }
        })

        if (this.action === 'create') {
          await helper.customPost(`${process.env.VUE_APP_API}/projects`, payload, { showToast: true })
        } else {
          await helper.customPatch(`${process.env.VUE_APP_API}/projects/${this.data.id}`, payload, { showToast: true })
        }
        this.$emit('refresh')
      } catch (error) {
        alert('Error saving project:', error)
      } finally {
        this.loading = false
      }
    },
    close() {
      this.$emit('modalClosed')
    },
    getAvailableUsers(currentIndex) {
      // Get IDs selected in other rows
      const selectedIds = this.form.team_members
        .filter((m, i) => i !== currentIndex && m.user_id)
        .map(m => m.user_id)
      
      // Return users not in that list (but include the current one if selected)
      return this.users.filter(u => !selectedIds.includes(u.id))
    },
    formatDateForInput(date) {
      if (!date) return ''
      const d = new Date(date)
      return d.toISOString().split('T')[0]
    }
  }
}
</script>
